import invariant from 'invariant';

import { SearchResults } from 'api/search';
import { FilestackImageWithTextData } from 'api/types/shared.types';
import algoliaSearch from 'utils/algoliaSearch';
import { trackEvent } from 'utils/analytics';
import { arrToObject, filterObject, isEmpty, values } from 'utils/functional';
import { getJson } from 'utils/http';
import getPostSearchIndex from 'utils/search/getPostSearchIndex';
import getSearchConfig from 'utils/search/getSearchConfig';
// Fix me
// eslint-disable-next-line import/no-cycle
import { postSearchFacetQueryFilters } from 'utils/search/getSearchQueryOptions';
import { initialFacetFiltersForPostSearch as initialFacetFilters } from 'utils/search/searchFacets';
import { truncateToUTF8Bytes } from 'utils/string';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle, no-restricted-imports
import { Dispatch, GetState } from '..';

// Types
//

type SetQueryAction = {
  type: 'POST_SEARCH_SET_QUERY';
  query: string;
};
type SearchSuccessAction = {
  type: 'POST_SEARCH_RESULTS_SUCCESS';
  results: PostSearchResults;
};
type SearchStartedAction = {
  type: 'POST_SEARCH_STARTED';
};
type SetPageIndexAction = {
  type: 'POST_SEARCH_SET_PAGE_INDEX';
  pageIndex: number;
};
type InitializeSearchAction = {
  type: 'POST_SEARCH_INITIALIZE';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>;
};
type ReinitializeSearchAction = {
  type: 'POST_SEARCH_REINITIALIZE';
};
type UninitializeSearchAction = {
  type: 'POST_SEARCH_UNINITIALIZE';
};
type ClearSearchResultsAction = {
  type: 'POST_SEARCH_CLEAR_RESULTS';
};
type ResetPageAction = {
  type: 'POST_SEARCH_RESET_PAGE';
};
type ResetFiltersAction = {
  type: 'POST_SEARCH_RESET_FILTERS';
};
type SetSearchFilter = {
  type: 'POST_SEARCH_SET_FILTER';
  key: string;
  value: string | null | undefined;
};
type SelectFilterOption = {
  type: 'POST_SEARCH_SELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};
type DeselectFilterOption = {
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};
type SelectAllFilterOption = {
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS';
  key: string;
};
type SetFacetOptions = {
  type: 'POST_SEARCH_SET_FACET_OPTIONS';
  facetKey: string;
  options: Array<string>;
  optionTitles: Record<string, string>;
  optionsArray: Array<{
    id: string;
    parentId: string | null | undefined;
    slug: string;
    name: string;
  }>;
  reload?: boolean;
};
export type Action =
  | SetQueryAction
  | SearchSuccessAction
  | SearchStartedAction
  | SetPageIndexAction
  | InitializeSearchAction
  | ReinitializeSearchAction
  | UninitializeSearchAction
  | ClearSearchResultsAction
  | ResetPageAction
  | ResetFiltersAction
  | SelectAllFilterOption
  | DeselectFilterOption
  | SetSearchFilter
  | SelectFilterOption
  | SetFacetOptions;

export type PostHit = {
  author:
    | {
        imageHandle: string | null | undefined;
        name: string;
        slug: string;
      }
    | null
    | undefined;
  categories: Array<string>;
  content: string;
  headerImage: FilestackImageWithTextData;
  locale: UserLocale;
  objectID: string;
  postedDate: string;
  showDate: boolean;
  subsiteId: Array<string>;
  subsiteName: string;
  tags: Array<string>;
  thumbnail: FilestackImageWithTextData;
  title: string;
  url: string;
  type: string;
  metaDescription: string | null | undefined;
};

export type PostSearchResults = Omit<SearchResults, 'hits'> & {
  hits: Array<PostHit>;
};

type PostSearchFilters = {
  articleAuthorFacet: string;
  articleTagsFacet: string[];
  articleCategoriesFacet: string[];
};

const postSearchFacets: Record<string, SearchFacet> = {
  articleCategoriesFacet: {
    id: 'articleCategoriesFacet',
    name: 'categories',
    title: getText('Category'),
    type: 'array',
    lazy: true,
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ id: string; name: string; title: string; t... Remove this comment to see the full error message
    optionsArray: [],
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
  articleAuthorFacet: {
    id: 'articleAuthorFacet',
    name: 'author.slug',
    title: getText('Author'),
    type: 'string',
    lazy: false,
    options: ['DUMMY_OPTION'],
    optionTitles: {},
  },
  articleTagsFacet: {
    id: 'articleTagsFacet',
    name: 'tags.slug',
    title: getText('Tags'),
    type: 'array',
    lazy: false,
    options: ['DUMMY_OPTION'],
    optionTitles: {},
  },
};
const initialFacetsThatArentLoaded: Array<string> = Object.keys(
  filterObject(postSearchFacets, (_, f) => !!f.lazy)
);
export type State = {
  query: string;
  pageIndex: number;
  lastLoadedPageIndex: number;
  searchId: number;
  // Incrementing id
  resultsByPage: Record<string, PostSearchResults>;
  resultsForSearchId?: string | null | undefined;
  initialized: boolean;
  initialSearchStarted: boolean;
  initialResultsLoaded: boolean;
  isSearching: boolean;
  queryID: string | null | undefined;
  searchFacets: Record<string, SearchFacet>;
  facetsThatArentLoaded: Array<string>;
  filters: PostSearchFilters;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postSearch?: any;
};
const initialSearchState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0,
  // Incrementing id
  resultsByPage: {},
  initialized: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  queryID: null,
  searchFacets: postSearchFacets,
  filters: {
    articleAuthorFacet: '',
    articleTagsFacet: [],
    articleCategoriesFacet: [],
  },
  facetsThatArentLoaded: initialFacetsThatArentLoaded,
};

// Reducer

const defaultState: State = initialSearchState;

export function postSearchReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: State = defaultState,
  action: Action
) {
  switch (action.type) {
    case 'POST_SEARCH_SET_QUERY': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        query: action.query,
      };
    }

    case 'POST_SEARCH_RESET_PAGE': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
      };
    }

    case 'POST_SEARCH_RESET_FILTERS': {
      const filters = {
        ...initialFacetFilters(
          values(state.searchFacets),
          state.facetsThatArentLoaded,
          {}
        ),
        articleCategoriesFacet: state.filters.articleCategoriesFacet,
      };
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters,
      };
    }

    case 'POST_SEARCH_SET_PAGE_INDEX': {
      return { ...state, pageIndex: action.pageIndex };
    }

    case 'POST_SEARCH_INITIALIZE': {
      const { page, pq } = action.queryParams;
      const filters: PostSearchFilters = initialFacetFilters(
        values(postSearchFacets),
        initialFacetsThatArentLoaded,
        action.queryParams
      );
      return {
        ...initialSearchState,
        initialized: true,
        query: pq ? truncateToUTF8Bytes(pq || '', 512) : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        filters,
      };
    }

    case 'POST_SEARCH_REINITIALIZE': {
      const filters: PostSearchFilters = initialFacetFilters(
        values(state.searchFacets),
        state.facetsThatArentLoaded,
        {
          pq: '',
        }
      );
      return { ...state, initialized: true, query: '', pageIndex: 0, filters };
    }

    case 'POST_SEARCH_UNINITIALIZE': {
      return {
        ...state,
        facetsThatArentLoaded: initialFacetsThatArentLoaded,
        initialized: false,
      };
    }

    case 'POST_SEARCH_STARTED': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null,
      };
    }

    case 'POST_SEARCH_RESULTS_SUCCESS': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];

      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;
      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results,
            }
          : { ...state.resultsByPage, [action.results.page]: action.results },
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false,
      };
    }

    case 'POST_SEARCH_CLEAR_RESULTS': {
      return { ...state, resultsByPage: {}, resultsForSearchId: null };
    }

    case 'POST_SEARCH_SET_FACET_OPTIONS': {
      const { options, optionTitles, optionsArray, facetKey, reload } = action;
      const facet = state.searchFacets[facetKey];
      const facetsThatArentLoaded: Array<string> =
        state.facetsThatArentLoaded.filter((key) => key !== facetKey);

      if (!facet && isEmpty(options)) {
        return state;
      }

      // const sideFacets = sideFacetsForType(searchFacets, state.filters.type);
      // const hasFacet = sideFacets.find(f => f.id === facet.id);
      const filterValue = facet.id && state.filters[facet.id];
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      const updatedFilters = reload
        ? { ...state.filters, articleCategoriesFacet: [] }
        : facet.id // && hasFacet
        ? { ...state.filters, [facet.id]: filterValue || options }
        : state.filters;
      const newSearchFacets: Record<string, SearchFacet> = {
        // Consider all search facets, not just ones in the current state
        ...postSearchFacets,
        // $FlowFixMe InferError
        ...state.searchFacets,
        [facetKey]: {
          ...facet,
          options,
          optionTitles,
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ [x: string]: SearchFacet | { options: stri... Remove this comment to see the full error message
          optionsArray,
        },
      };
      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters,
        searchFacets: newSearchFacets,
      };
    }

    case 'POST_SEARCH_SET_FILTER': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const value = action.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-object-spread
      const filters = Object.assign(
        {}, // Setting the type filter resets the other filters.
        state.filters,
        {
          [facet]: value,
        }
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters,
      };
    }

    case 'POST_SEARCH_SELECT_FILTER_OPTION': {
      const facet = action.key;
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option,
      ];
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'POST_SEARCH_DESELECT_FILTER_OPTION': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const option = action.option;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      const selectedOptions = (state.filters[facet] as string[]).filter(
        (v) => v !== option
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: {
          ...state.filters,
          [facet]: state.searchFacets[facet].options,
        },
      };
    }

    default:
      return state;
  }
}

// Utility operations on state

export const resultsFn = (state: State) =>
  state.resultsByPage[state.pageIndex.toString()];

const executeSearchQuery = (
  {
    state: {
      postSearch: {
        pageIndex: pageIndexState,
        query,
        searchId,
        filters,
        searchFacets,
      },
    },
    pageIndex,
    subsiteId,
    includedSubsiteIds,
    locale,
  }: {
    state: State;
    pageIndex?: number;
    subsiteId: string;
    includedSubsiteIds: Array<string>;
    locale: string;
  } // TODO: Fix this the next time the file is edited.
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  const timestamp = new Date();
  let index = getPostSearchIndex();

  // use replica that's sorted differently when query is provided by user input
  if (query && query.length > 0 && index !== null && index !== undefined) {
    index = `${index}-relevant`;
  }

  invariant(index, 'search index not retreived');
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign
  pageIndex = pageIndex || pageIndexState;
  const searchFilters = postSearchFacetQueryFilters(
    filters,
    searchFacets,
    locale
  );
  const subsitesToSearch = [...includedSubsiteIds, subsiteId].map(
    (id) => `subsiteId:${id}`
  );
  const queryOptions = {
    index,
    hitsPerPage: 20,
    page: pageIndex || 0,
    facets: '*,subsiteId,pageType,categoryIds,locale',
    facetFilters: [subsitesToSearch],
    filters: searchFilters.join(' AND '),
    attributesToSnippet: ['description:20'],
    attributesToRetrieve: '*',
    // TODO: list attributes,
    removeStopWords: true,
    ignorePlurals: true,
    advancedSyntax: true,
    queryLanguages: [locale],
  };
  const searchConfig = getSearchConfig();
  return algoliaSearch(searchConfig, query, queryOptions).then((results) => ({
    ...results,
    timestamp,
    searchId,
    // This is for analytics
    searchParameters: {
      query,
      queryOptions,
    },
  }));
};

export const setSearchQuery =
  (query: string, didUserChange: boolean) =>
  (
    dispatch: Dispatch // getState: GetState
  ) => {
    if (didUserChange) {
      trackEvent('Articles: Changed Search Query', {
        new_query: query, // TODO: add tracking data for POST
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_QUERY',
      query,
    });
  };
export const loadDynamicFacetOptions =
  (subsiteId: string, reload?: boolean) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const facetsThatArentLoaded = reload
      ? [...state.postSearch.facetsThatArentLoaded, 'articleCategoriesFacet']
      : state.postSearch.facetsThatArentLoaded;
    return Promise.all(
      [
        {
          fetchFn: () =>
            getJson(
              `/data/website/subsites/${subsiteId}/categories?lang=${CURRENT_LOCALE}`
            ),
          facetKey: 'articleCategoriesFacet',
          responseToOptions: ({ categories }) => categories,
        },
      ].map(({ facetKey, fetchFn, responseToOptions }) => {
        if (!facetsThatArentLoaded.includes(facetKey)) {
          return Promise.resolve();
        }

        return (
          fetchFn()
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
            .then((response) => responseToOptions(response))
            .then((options) => {
              dispatch({
                type: 'POST_SEARCH_SET_FACET_OPTIONS',
                facetKey,
                options: options.map((f) => f.slug),
                optionTitles: arrToObject(
                  options.map(({ slug, name }) => [slug, name])
                ),
                optionsArray: options.map(({ slug, id, parentId, name }) => ({
                  id,
                  name,
                  slug,
                  parentId,
                })),
                reload,
              });
            })
        );
      })
    );
  };
export const initializeSearch =
  // @ts-expect-error ts-migrate(2739) FIXME: Type '{}' is missing the following properties from... Remove this comment to see the full error message


    ({
      queryParams,
      subsiteId,
    }: {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryParams: Record<string, any>;
      subsiteId: string;
    } = {}) =>
    (dispatch: Dispatch) =>
      Promise.all([
        dispatch({
          type: 'POST_SEARCH_INITIALIZE',
          queryParams,
        }),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(loadDynamicFacetOptions(subsiteId)),
      ]);
export const reinitializeSearch =
  // @ts-expect-error ts-migrate(2741) FIXME: Property 'subsiteId' is missing in type '{}' but r... Remove this comment to see the full error message


    ({
      subsiteId,
    }: {
      subsiteId: string;
    } = {}) =>
    (dispatch: Dispatch) =>
      Promise.all([
        dispatch({
          type: 'POST_SEARCH_REINITIALIZE',
        }),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(loadDynamicFacetOptions(subsiteId, true)),
      ]);
export const uninitializeSearch = () => (dispatch: Dispatch) =>
  dispatch({
    type: 'POST_SEARCH_UNINITIALIZE',
  });
export const setPageIndex =
  (pageIndex: number, didUserChange: boolean) =>
  (
    dispatch: Dispatch // getState: GetState
  ) => {
    if (didUserChange) {
      trackEvent('POST: Changed Search Page Number', {
        new_page: pageIndex, // TODO: tracking data
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_PAGE_INDEX',
      pageIndex,
    });
  };

const searchResultsSuccessful = ({
  results,
}: {
  results: PostSearchResults;
}): SearchSuccessAction => ({
  type: 'POST_SEARCH_RESULTS_SUCCESS',
  results,
});

export const search =
  (subsiteId: string, includedSubsiteIds: Array<string>, locale: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: 'POST_SEARCH_STARTED',
    });
    executeSearchQuery({
      // @ts-expect-error fix me
      state: getState(),
      subsiteId,
      includedSubsiteIds,
      locale,
    }).then((results) => {
      dispatch(
        searchResultsSuccessful({
          results,
        })
      );
    });
  };
export const setSearchFilter = (
  filterKey: string,
  value: string | null | undefined
): SetSearchFilter => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }

  return {
    type: 'POST_SEARCH_SET_FILTER',
    key: filterKey,
    value,
  };
};
export const selectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined
): SelectFilterOption => ({
  type: 'POST_SEARCH_SELECT_FILTER_OPTION',
  key: filterKey,
  option,
});
export const deselectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined
): DeselectFilterOption => ({
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION',
  key: filterKey,
  option,
});
export const selectAllSearchFilterOptions = (
  filterKey: string
): SelectAllFilterOption => ({
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: filterKey,
});
export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'POST_SEARCH_RESET_FILTERS',
});
